import type { ExportFormat, PreviewAspect } from '@/lib/export/resolveExportFormat';

export function resolveLayoutExportFormats(
  { availableFormats, requiredAspect }: {
    availableFormats: ExportFormat[];
    requiredAspect?: PreviewAspect;
  },
): ExportFormat[] {
  // 16:9 is always offered. Portrait-only layouts still keep vertical, and
  // switching to landscape warns that Single will be used.
  if (requiredAspect === 'landscape') return ['horizontal'];
  if (availableFormats.includes('horizontal')) return availableFormats;
  return ['horizontal', ...availableFormats];
}

/**
 * The aspect the preview actually renders at.
 *
 * Layout geometry still wins while a portrait-only post layout is active.
 * 16:9 is never clamped away for a vertical source — the user can always
 * preview and export landscape (pillarboxed). Portrait can still fall back
 * to landscape when the source cannot produce vertical.
 *
 * Insert-time layer fitting and the preview canvas must both read this, or a
 * layer gets fitted against the wrong composition aspect.
 */
export function resolveEffectivePreviewAspect(
  { previewAspectRatio, availableFormats, requiredAspect }: {
    previewAspectRatio: PreviewAspect;
    availableFormats: ExportFormat[];
    requiredAspect?: PreviewAspect;
  },
): PreviewAspect {
  // Layout geometry wins over source metadata until the user confirms
  // switching to Single for 16:9. A vertical source can still preview and
  // export landscape (pillarboxed); never clamp that request away.
  if (requiredAspect) return requiredAspect;
  if (previewAspectRatio === 'portrait' && !availableFormats.includes('vertical')) {
    return 'landscape';
  }
  return previewAspectRatio;
}

/**
 * Warn that the 9:16 framing isn't final: the user is previewing portrait from
 * a horizontal source, and the auto-crop keyframes haven't been computed yet.
 * A vertical source needs no crop, so it never warns.
 */
export function shouldShowAutoCropPendingNotice(
  { effectiveAspect, hasCropKeyframes, availableFormats }: {
    effectiveAspect: PreviewAspect;
    hasCropKeyframes: boolean;
    availableFormats: ExportFormat[];
  },
): boolean {
  if (effectiveAspect !== 'portrait') return false;
  if (hasCropKeyframes) return false;
  // A vertical-only source is already 9:16; nothing to crop.
  return availableFormats.includes('horizontal');
}
